import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services/orderService';
import { Order } from '../types';

const OrdersPage: React.FC = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchOrders();
    }, [currentPage]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await orderService.getUserOrders(currentPage, 10);
            setOrders(response.orders);
            setTotalPages(response.pagination.totalPages);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur lors du chargement des commandes');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'confirmed':
                return 'bg-blue-100 text-blue-800';
            case 'preparing':
                return 'bg-purple-100 text-purple-800';
            case 'shipped':
                return 'bg-indigo-100 text-indigo-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
                return 'En attente';
            case 'confirmed':
                return 'Confirmée';
            case 'preparing':
                return 'En préparation';
            case 'shipped':
                return 'Expédiée';
            case 'delivered':
                return 'Livrée';
            case 'cancelled':
                return 'Annulée';
            default:
                return status;
        }
    };

    if (loading && orders.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-eldai-blue text-xl">Chargement des commandes...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-eldai-blue mb-2">
                            Mes commandes
                        </h1>
                        <p className="text-eldai-gray">
                            Retrouvez l'historique de vos commandes
                        </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                        <Link
                            to="/catalog"
                            className="bg-eldai-blue text-white px-6 py-2 rounded-lg font-semibold hover:bg-eldai-light-blue transition-colors"
                        >
                            Continuer les achats
                        </Link>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                        {error}
                    </div>
                )}

                {orders.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-4">📦</div>
                        <h2 className="text-2xl font-bold text-eldai-blue mb-4">
                            Aucune commande trouvée
                        </h2>
                        <p className="text-eldai-gray mb-8">
                            Vous n'avez pas encore passé de commande
                        </p>
                        <Link
                            to="/catalog"
                            className="bg-eldai-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-eldai-light-blue transition-colors"
                        >
                            Découvrir nos produits
                        </Link>
                    </div>
                ) : (
                    <>
                        {/* Orders List */}
                        <div className="space-y-4">
                            {orders.map((order) => (
                                <div key={order.id} className="bg-white rounded-lg shadow-md p-6">
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-4 mb-2">
                                                <h3 className="text-lg font-semibold text-eldai-blue">
                                                    Commande #{order.order_number}
                                                </h3>
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                                                    {getStatusText(order.status)}
                                                </span>
                                            </div>
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 text-sm text-eldai-gray">
                                                <span>📅 {formatDate(order.created_at)}</span>
                                                <span>📦 {order.total_items} article{order.total_items > 1 ? 's' : ''}</span>
                                                <span>🏠 {order.delivery_city}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
                                            <div className="text-right">
                                                <div className="text-lg font-bold text-eldai-blue">
                                                    {formatPrice(order.total_amount)}
                                                </div>
                                                <div className="text-sm text-eldai-gray">
                                                    Total TTC
                                                </div>
                                            </div>
                                            <Link
                                                to={`/orders/${order.id}`}
                                                className="bg-eldai-yellow text-eldai-blue px-4 py-2 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors"
                                            >
                                                Voir détails
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 mt-8">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 rounded-lg border border-eldai-gray text-eldai-blue hover:bg-eldai-gray-light disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Précédent
                                </button>
                                <span className="px-4 py-2 text-eldai-blue">
                                    Page {currentPage} sur {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 rounded-lg border border-eldai-gray text-eldai-blue hover:bg-eldai-gray-light disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default OrdersPage;
